import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import _ from 'underscore';

import HideValidationPopupsContext from 'sharedreact/contexts/HideValidationPopupsContext';
import Tooltip from 'sharedreact/components/Tooltip';
import { getValidationRules } from 'sharedreact/utils/ValidationRules';
import { useFormComponents, useValidate } from 'sharedreact/components/ValidationForm';


type Props = {
  className?: string;
  containerClassName?: string;
  id: string;
  type?: React.HTMLInputTypeAttribute;
  value: string | number;
  checked?: boolean;
  validationTooltipPlacement?: string;
  validationTooltipClassName?: string;
  placeholder?: string;
  disabled?: boolean;
  name: string;
  autoComplete?: string;
  errorAnchorElement?: HTMLElement | null;
  inputRef?: (element: HTMLInputElement) => void;
  validations: string[];

  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
};

function ValidationInput ({
  className,
  containerClassName,
  id,
  type,
  value,
  checked,
  validationTooltipPlacement = 'auto right',
  validationTooltipClassName,
  placeholder,
  disabled,
  name,
  autoComplete,
  errorAnchorElement,
  inputRef,
  onBlur,
  onChange,
  onKeyDown,
  onFocus,
  validations
}: Props) {
  const rules = getValidationRules();

  const [components, ComponentActions] = useFormComponents();
  
  useEffect(() => {
    ComponentActions.add(name, value);

    return () => ComponentActions.remove(name);
  }, []);

  const isInitialCall = useRef(true);

  useEffect(() => {
    if (!isInitialCall.current) {
      ComponentActions.update(name, value);
    }
    isInitialCall.current = false;
  }, [value]);

  const hideValidationPopups = useContext(HideValidationPopupsContext);

  const input = useRef<HTMLInputElement | null>(null);

  const [hint, validate] = useValidate(validations, rules, name);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    validate(type === 'checkbox' ? event.target.checked : event.target.value, components);

    if (onBlur != null) {
      onBlur(event);
    }
  }, [validate, onBlur, components]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    validate(type === 'checkbox' ? event.target.checked : event.target.value, components);

    if (onChange != null) {
      onChange(event);
    }
  }, [validate, onChange, components]);

  return (
      <div
        className={classNames(
          containerClassName,
          { 'has-error': hint },
          { 'has-feedback': hint }
        )}
      >
        <Tooltip show={hint != null && !hideValidationPopups} title={hint}
          className={validationTooltipClassName} placement={validationTooltipPlacement}
          parent={errorAnchorElement ?? input.current}
        />
        <input
          className={classNames('esko_form-control', className)}
          id={id}
          type={type}
          value={value}
          checked={checked}
          placeholder={placeholder}
          disabled={disabled}
          name={name}
          autoComplete={autoComplete}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          ref={(ref) => {
            input.current = ref;
            if (inputRef != null && ref != null) {
              inputRef(ref);
            }
          }} />
      </div>
    );
}

export default ValidationInput;
